import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from "react-router";
import { useQuery } from '@tanstack/react-query';
import fetchSearchResults from '../services/fetchSearchResults';
import fetchProgramSearchResults from '../services/fetchProgramSearchResults';
import fetchFacultySearchResults from '../services/fetchFacultySearchResults';
import fetchMagazineSearchResults from '../services/fetchMagazineSearchResults';
import { Card } from 'primereact/card';
import { Skeleton } from 'primereact/skeleton';
import { TabView, TabPanel } from 'primereact/tabview';
import { InputText } from "primereact/inputtext";
import { FloatLabel } from "primereact/floatlabel";
import { useDebouncedCallback } from 'use-debounce';
import { CMSSearchResult } from '../types/CMSSearchResult';
import { CMSLink } from '../../../myknox/scripts/types/CMSLink';
import fetchTopSearchTerms from '../services/fetchTopSearchTerms';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';


const App: React.FC<any> = () => {
    const [params, setParams] = useSearchParams();
    const terms = params.get("terms");
    const page = Number(params.get("page") ?? 1);
    const fpage = Number(params.get("fpage") ?? 1);
    const mpage = Number(params.get("mpage") ?? 1);
    const ppage = Number(params.get("ppage") ?? 1);
    const [searchResults, setSearchResults] = useState<CMSSearchResult>();
    const [facultyResults, setFacultyResults] = useState<CMSSearchResult>();
    const [magazineResults, setMagazineResults] = useState<CMSSearchResult>();
    const [programResults, setProgramResults] = useState<CMSSearchResult>();
    const [topSearchTerms, setTopSearchTerms] = useState<CMSLink[]>([]);
    const [searchPaginatorFirst, setSearchPaginatorFirst] = useState<number>(0);
    const [searchResultRows, setSearchResultRows] = useState<number>(0);
    const [facultySearchPaginatorFirst, setFacultySearchPaginatorFirst] = useState<number>(0);
    const [facultySearchResultRows, setFacultySearchResultRows] = useState<number>(0);
    const [magazineSearchPaginatorFirst, setMagazineSearchPaginatorFirst] = useState<number>(0);
    const [magazineSearchResultRows, setMagazineSearchResultRows] = useState<number>(0);
    const [programSearchPaginatorFirst, setProgramSearchPaginatorFirst] = useState<number>(0);
    const [programSearchResultRows, setProgramSearchResultRows] = useState<number>(0);

    const {
        data: resultsData,
        isLoading: isResultsDataLoading,
        error: resultsDataError,
        refetch: refetchResultsData,
        isRefetching: isResultDataFetching
    } = useQuery({
        queryKey: ["searchResults", `${terms}${page ?? '1'}`],
        queryFn: () => fetchSearchResults(terms ?? '', page ?? 1),
        enabled: !!terms,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        refetchInterval: false,
        staleTime: Infinity
    });

    const {
        data: facultyResultsData,
        isLoading: isFacultyResultsDataLoading,
        error: facultyResultsDataError,
        refetch: refetchFacultyResultsData,
        isRefetching: isFacultyResultDataFetching
    } = useQuery({
        queryKey: ["facultySearchResults", `${terms}${fpage ?? '1'}`],
        queryFn: () => fetchFacultySearchResults(terms ?? '', fpage ?? 1),
        enabled: !!terms,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        refetchInterval: false,
        staleTime: Infinity
    });

    const {
        data: magazineResultsData,
        isLoading: isMagazineResultsDataLoading,
        error: magazineResultsDataError,
        refetch: refetchMagazineResultsData,
        isRefetching: isMagazineResultDataFetching
    } = useQuery({
        queryKey: ["magazineSearchResults", `${terms}${mpage ?? '1'}`],
        queryFn: () => fetchMagazineSearchResults(terms ?? '', mpage ?? 1),
        enabled: !!terms,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        refetchInterval: false,
        staleTime: Infinity
    });

    const {
        data: programResultsData,
        isLoading: isProgramResultsDataLoading,
        error: programResultsDataError,
        refetch: refetchProgramResultsData,
        isRefetching: isProgramResultDataFetching
    } = useQuery({
        queryKey: ["programSearchResults", `${terms}${ppage ?? '1'}`],
        queryFn: () => fetchProgramSearchResults(terms ?? '', ppage ?? 1),
        enabled: !!terms,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        refetchInterval: false,
        staleTime: Infinity
    });

    const {
        data: topTermsData,
        isLoading: isTopTermsDataLoading,
        error: topTermsDataError
    } = useQuery({
        queryKey: ["TopTerms"],
        queryFn: () => fetchTopSearchTerms(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        refetchInterval: false,
        staleTime: Infinity
    });

    useEffect(() => {
        if (resultsData) {
            setSearchResults(resultsData);
            setSearchResultRows(Number(resultsData.SearchResults.ResultsCount));
            setSearchPaginatorFirst((Number(page) - 1) * 10);
        }
    }, [resultsData]);

    useEffect(() => {
        if (facultyResultsData) {
            setFacultyResults(facultyResultsData);
            setFacultySearchResultRows(Number(facultyResultsData.SearchResults.ResultsCount));
            setFacultySearchPaginatorFirst((Number(fpage) - 1) * 10);

            console.log(facultyResultsData);
        }
    }, [facultyResultsData]);

    useEffect(() => {
        if (magazineResultsData) {
            setMagazineResults(magazineResultsData);
            setMagazineSearchResultRows(Number(magazineResultsData.SearchResults.ResultsCount));
            setMagazineSearchPaginatorFirst((Number(mpage) - 1) * 10);

            console.log(magazineResultsData);
        }
    }, [magazineResultsData]);

    useEffect(() => {
        if (programResultsData) {
            setProgramResults(programResultsData);
            setProgramSearchResultRows(Number(programResultsData.SearchResults.ResultsCount));
            setProgramSearchPaginatorFirst((Number(ppage) - 1) * 10);
        }
    }, [programResultsData]);

    useEffect(() => {
        if (topTermsData) {
            setTopSearchTerms(topTermsData);
        }
    }, [topTermsData]);

    const sendSearch = useDebouncedCallback(
        (value: string) => {
            setParams({
                terms: value,
                page: '1'
            });
        }, 500
    );

    const onSearchPageChange = (event: PaginatorPageChangeEvent) => {
        setSearchPaginatorFirst(event.first);

        setParams({
            terms: terms ?? '',
            page: `${event.page + 1}`
        });

        const el = document.getElementById("search-terms-input");
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const onFacultySearchPageChange = (event: PaginatorPageChangeEvent) => {
        setFacultySearchPaginatorFirst(event.first);

        setParams({
            terms: terms ?? '',
            fpage: `${event.page + 1}`
        });

        const el = document.getElementById("search-terms-input");
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const onMagazineSearchPageChange = (event: PaginatorPageChangeEvent) => {
        setMagazineSearchPaginatorFirst(event.first);

        setParams({
            terms: terms ?? '',
            mpage: `${event.page + 1}`
        });

        const el = document.getElementById("search-terms-input");
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const onProgramSearchPageChange = (event: PaginatorPageChangeEvent) => {
        setProgramSearchPaginatorFirst(event.first);

        setParams({
            terms: terms ?? '',
            ppage: `${event.page + 1}`
        });

        const el = document.getElementById("search-terms-input");
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <div className='kx-search-page'>
            <Card>
                <FloatLabel>
                    <InputText id="search-terms-input" defaultValue={terms ?? ''} onChange={(e) => sendSearch(e.target.value)} />
                    <label htmlFor="search-terms-input">Type here to search</label>
                </FloatLabel>
                {
                    (isResultsDataLoading || isResultDataFetching) ?
                        <>
                            <Skeleton width="10rem" className="kx-search-skeleton"></Skeleton>
                            <Skeleton width="5rem" className="kx-search-skeleton"></Skeleton>
                            <Skeleton height=".5rem" className='kx-search-skeleton'></Skeleton>
                            <Skeleton width="100%" height="150px" className='kx-search-skeleton'></Skeleton>
                        </>
                        : searchResults !== null ?
                            <TabView>
                                <TabPanel headerTemplate={(options) => {
                                    const { className, onClick, onKeyDown, selected, ariaControls, titleClassName } = options;

                                    return (
                                        <div
                                            className={className}
                                            onClick={onClick}
                                            onKeyDown={onKeyDown}
                                            role="tab"
                                            aria-selected={selected}
                                            aria-controls={ariaControls}
                                            tabIndex={0} // Important for keyboard accessibility
                                        >
                                            Pages <span>{searchResults?.SearchResults?.ResultsCount ?? ''}</span></div>)
                                }}>
                                    <div>
                                        {searchResults?.SearchResults?.Results?.Item?.map((result) =>
                                            <a key={result.PageID} href={result.URL} className='kx-search-result'>
                                                <h2>{result.Title}</h2>
                                                <p dangerouslySetInnerHTML={{
                                                    __html: result.MatchingFrags.replace(/\/(?!strong\b)[A-Za-z0-9/_\-.]+/g, "")
                                                        .replace(/\s{2,}/g, " ")
                                                        .trim()
                                                }}></p>
                                                <p>{`${KNOX_SITE_ROOT}${result.URL}`}</p>
                                            </a>
                                        )}
                                    </div>
                                    <Paginator first={searchPaginatorFirst} rows={10} totalRecords={searchResultRows} onPageChange={onSearchPageChange} />
                                </TabPanel>
                                <TabPanel headerTemplate={(options) => {
                                    const { className, onClick, onKeyDown, selected, ariaControls, titleClassName } = options;

                                    return (
                                        <div
                                            className={className}
                                            onClick={onClick}
                                            onKeyDown={onKeyDown}
                                            role="tab"
                                            aria-selected={selected}
                                            aria-controls={ariaControls}
                                            tabIndex={0} // Important for keyboard accessibility
                                        >
                                            Programs <span>{programResults?.SearchResults?.ResultsCount ?? ''}</span></div>)
                                }}>
                                    {(isProgramResultsDataLoading || isProgramResultDataFetching) ?
                                        <>
                                            <Skeleton width="10rem" className="kx-search-skeleton"></Skeleton>
                                            <Skeleton width="5rem" className="kx-search-skeleton"></Skeleton>
                                            <Skeleton height=".5rem" className='kx-search-skeleton'></Skeleton>
                                            <Skeleton width="100%" height="150px" className='kx-search-skeleton'></Skeleton>
                                        </>
                                        : searchResults !== null ?
                                            <>
                                                <div>
                                                    {programResults?.SearchResults?.Results?.Item?.map((result) =>
                                                        <a key={result.PageID} href={result.URL} className='kx-search-result'>
                                                            <h2>{result.Title}</h2>
                                                            <p dangerouslySetInnerHTML={{
                                                                __html: result.MatchingFrags.replace(/\/(?!strong\b)[A-Za-z0-9/_\-.]+/g, "")
                                                                    .replace(/\s{2,}/g, " ")
                                                                    .trim()
                                                            }}></p>
                                                            <p>{`${KNOX_SITE_ROOT}${result.URL}`}</p>
                                                        </a>
                                                    )}
                                                </div>
                                                <Paginator first={programSearchPaginatorFirst} rows={10} totalRecords={programSearchResultRows} onPageChange={onProgramSearchPageChange} />
                                            </> : <h2>No programs results found.</h2>}
                                </TabPanel>
                                <TabPanel headerTemplate={(options) => {
                                    const { className, onClick, onKeyDown, selected, ariaControls, titleClassName } = options;

                                    return (
                                        <div
                                            className={className}
                                            onClick={onClick}
                                            onKeyDown={onKeyDown}
                                            role="tab"
                                            aria-selected={selected}
                                            aria-controls={ariaControls}
                                            tabIndex={0} // Important for keyboard accessibility
                                        >
                                            Faculty <span>{facultyResults?.SearchResults?.ResultsCount ?? ''}</span></div>)
                                }}>
                                    {(isFacultyResultsDataLoading || isFacultyResultDataFetching) ?
                                        <>
                                            <Skeleton width="10rem" className="kx-search-skeleton"></Skeleton>
                                            <Skeleton width="5rem" className="kx-search-skeleton"></Skeleton>
                                            <Skeleton height=".5rem" className='kx-search-skeleton'></Skeleton>
                                            <Skeleton width="100%" height="150px" className='kx-search-skeleton'></Skeleton>
                                        </>
                                        : searchResults !== null ?
                                            <>
                                                <div>
                                                    {facultyResults?.SearchResults?.Results?.Item?.map((result) =>
                                                        <a key={result.PageID} href={result.URL} className='kx-search-result'>
                                                            <h2>{result.Title}</h2>
                                                            <p dangerouslySetInnerHTML={{
                                                                __html: result.MatchingFrags.replace(/\/(?!strong\b)[A-Za-z0-9/_\-.]+/g, "")
                                                                    .replace(/\s{2,}/g, " ")
                                                                    .trim()
                                                            }}></p>
                                                            <p>{`${KNOX_SITE_ROOT}${result.URL}`}</p>
                                                        </a>
                                                    )}
                                                </div>
                                                <Paginator first={facultySearchPaginatorFirst} rows={10} totalRecords={facultySearchResultRows} onPageChange={onFacultySearchPageChange} />
                                            </> : <h2>No faculty found.</h2>}
                                </TabPanel>
                                <TabPanel headerTemplate={(options) => {
                                    const { className, onClick, onKeyDown, selected, ariaControls, titleClassName } = options;

                                    return (
                                        <div
                                            className={className}
                                            onClick={onClick}
                                            onKeyDown={onKeyDown}
                                            role="tab"
                                            aria-selected={selected}
                                            aria-controls={ariaControls}
                                            tabIndex={0} // Important for keyboard accessibility
                                        >
                                            Magazine <span>{magazineResults?.SearchResults?.ResultsCount ?? ''}</span></div>)
                                }}>
                                    {(isMagazineResultsDataLoading || isMagazineResultDataFetching) ?
                                        <>
                                            <Skeleton width="10rem" className="kx-search-skeleton"></Skeleton>
                                            <Skeleton width="5rem" className="kx-search-skeleton"></Skeleton>
                                            <Skeleton height=".5rem" className='kx-search-skeleton'></Skeleton>
                                            <Skeleton width="100%" height="150px" className='kx-search-skeleton'></Skeleton>
                                        </>
                                        : searchResults !== null ?
                                            <>
                                                <div>
                                                    {magazineResults?.SearchResults?.Results?.Item?.map((result) =>
                                                        <a key={result.PageID} href={result.URL} className='kx-search-result'>
                                                            <h2>{result.Title}</h2>
                                                            <p dangerouslySetInnerHTML={{
                                                                __html: result.MatchingFrags.replace(/\/(?!strong\b)[A-Za-z0-9/_\-.]+/g, "")
                                                                    .replace(/\s{2,}/g, " ")
                                                                    .trim()
                                                            }}></p>
                                                            <p>{`${KNOX_SITE_ROOT}${result.URL}`}</p>
                                                        </a>
                                                    )}
                                                </div>
                                                <Paginator first={magazineSearchPaginatorFirst} rows={10} totalRecords={magazineSearchResultRows} onPageChange={onMagazineSearchPageChange} />
                                            </> : <h2>No magazine results found.</h2>}
                                </TabPanel>
                            </TabView> :
                            <h2>No results found.</h2>
                }
            </Card>
            <div>
                <Card className='kx-search-top-terms'>
                    <h2>Top Searches</h2>
                    <ul>
                        {!isTopTermsDataLoading ?
                            topSearchTerms.map((topSearchTerm) =>
                                <li key={`${topSearchTerm.id}${topSearchTerm.name}`}><a href={topSearchTerm.url}>{topSearchTerm.name}</a></li>
                            )
                            :
                            <li>
                                <Skeleton width="10rem" className="kx-search-skeleton"></Skeleton>
                                <Skeleton width="5rem" className="kx-search-skeleton"></Skeleton>
                            </li>}
                    </ul>
                </Card>
                {/*<Card>*/}
                {/*    <h2>Recent Searches</h2>*/}
                { }
            </div >
        </div >
    )
}

export default App;